import { Outlet, Link } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { useOnboardingState } from '@/contexts/OnboardingContext';
import { deriveBotStatus, type BotStatus } from '@/lib/botStatus';
import { cn } from '@/lib/utils';
import { HIT_AREA_44 } from '@/lib/touchTarget';
import { billingService, type UsageResponse } from '@/services/billingService';
import {
  botStatusService,
  type BotOperationalStatus,
  type BotStatusReason,
} from '@/services/botStatusService';
import { UsageMeter } from '@/components/billing/UsageMeter';
import { NotificationBell } from './NotificationBell';
import { Sidebar } from './Sidebar';
import { UserMenu } from './UserMenu';
import { SupportMenu } from './SupportMenu';
import { VerificationBanner } from './VerificationBanner';

// ── Neutral loading placeholder chip (F067) ────────────────────────────────

function LoadingChip() {
  const { t } = useTranslation();
  return (
    <div
      data-testid="bot-status-chip"
      aria-label={t('bot.status.aria_loading')}
      className={cn(
        'inline-flex items-center rounded-full bg-muted transition-colors',
        'h-8 w-8 justify-center md:w-auto md:gap-2 md:px-3 md:justify-start'
      )}
    >
      <span
        className="h-2 w-2 rounded-full bg-muted-foreground/40"
        aria-hidden="true"
        data-testid="bot-status-chip-dot"
      />
    </div>
  );
}

// ── Pre-live onboarding chip (unchanged from F031/F053) ────────────────────

function OnboardingChip() {
  const { t } = useTranslation();
  const { state } = useOnboardingState();
  const status = deriveBotStatus(state);

  const labelKey: Record<BotStatus, string> = {
    setup: 'bot.status.setup',
    preview_tested: 'bot.status.preview_tested',
    connected: 'bot.status.connected',
    live: 'bot.status.live',
  };
  const ariaKey: Record<BotStatus, string> = {
    setup: 'bot.status.aria_setup',
    preview_tested: 'bot.status.aria_preview_tested',
    connected: 'bot.status.aria_connected',
    live: 'bot.status.aria_live',
  };
  const label = t(labelKey[status]);
  const ariaLabel = `${label} — ${t(ariaKey[status])}`;

  return (
    <Link
      to="/"
      aria-label={ariaLabel}
      data-testid="bot-status-chip"
      data-status={status}
      className={cn(
        'inline-flex items-center rounded-full bg-muted hover:bg-muted/60 transition-colors',
        // Mobile: 32x32 dot-only; desktop: full chip with label
        'h-8 w-8 justify-center md:w-auto md:gap-2 md:px-3 md:justify-start',
        // F098: ≥44px tap target without growing the 32px dot
        HIT_AREA_44
      )}
    >
      <span
        className={cn('h-2 w-2 rounded-full bg-warning')}
        aria-hidden="true"
        data-testid="bot-status-chip-dot"
      />
      <span className="hidden md:inline text-xs font-medium">{label}</span>
    </Link>
  );
}

// ── Live operational chip (F045) ────────────────────────────────────────────

/** Returns true when the reason is an auto/offline block that the owner cannot
 *  resume themselves (resume clears the manual flag but cannot override quota /
 *  expired token). Manual pause is always resumable. */
function isAutoBlock(reason: BotStatusReason): boolean {
  return (
    reason === 'quota_cap' ||
    reason === 'trial_cap' ||
    reason === 'no_subscription' ||
    reason === 'token_expired' ||
    reason === 'no_integration'
  );
}

function OperationalChip() {
  const { t } = useTranslation();
  const [opStatus, setOpStatus] = useState<BotOperationalStatus>('active');
  const [reason, setReason] = useState<BotStatusReason>(null);
  const [usage, setUsage] = useState<UsageResponse | null>(null);
  const [isPausing, setIsPausing] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  // F067: track whether the initial getStatus() fetch has settled so we never
  // flash the 'active' default or show 'Offline' while the call is in-flight.
  const [statusFetched, setStatusFetched] = useState(false);
  const initialFetchRef = useRef(true);

  const fetchStatus = useCallback(() => {
    botStatusService
      .getStatus()
      .then((res) => {
        setOpStatus(res.status);
        setReason(res.reason);
      })
      .catch(() => {
        // graceful: keep previous state on error
      })
      .finally(() => {
        if (initialFetchRef.current) {
          initialFetchRef.current = false;
          setStatusFetched(true);
        }
      });
  }, []);

  useEffect(() => {
    fetchStatus();
    billingService
      .getUsage()
      .then(setUsage)
      .catch(() => {
        // graceful: show nothing on error
      });
  }, [fetchStatus]);

  // Re-fetch on window focus (no websocket)
  useEffect(() => {
    window.addEventListener('focus', fetchStatus);
    return () => window.removeEventListener('focus', fetchStatus);
  }, [fetchStatus]);

  const dotClass =
    opStatus === 'active' ? 'bg-success' : opStatus === 'paused' ? 'bg-warning' : 'bg-destructive';

  const operationalLabel = t(`bot.status.operational.${opStatus}`);
  const blocked = isAutoBlock(reason);

  async function handlePause() {
    setIsPausing(true);
    try {
      const res = await botStatusService.pause();
      setOpStatus(res.status);
      setReason(res.reason);
    } catch {
      // graceful
    } finally {
      setIsPausing(false);
    }
  }

  async function handleResume() {
    setIsResuming(true);
    try {
      const res = await botStatusService.resume();
      setOpStatus(res.status);
      setReason(res.reason);
    } catch {
      // graceful
    } finally {
      setIsResuming(false);
    }
  }

  // F067: while the initial getStatus() is in-flight, show the neutral chip
  // to avoid flashing the destructive 'Offline' default.
  if (!statusFetched) {
    return <LoadingChip />;
  }

  // Billing/reconnect CTAs based on reason
  const billingReasons: BotStatusReason[] = ['quota_cap', 'trial_cap', 'no_subscription'];
  const reconnectReasons: BotStatusReason[] = ['token_expired', 'no_integration'];
  const showBillingCta =
    billingReasons.includes(reason) || (usage !== null && opStatus !== 'offline');
  const showReconnectCta = reconnectReasons.includes(reason);

  return (
    <DropdownMenu>
      {/* F092 (A02): render the trigger as Radix's own native <button> (no
          `asChild` nesting a custom element) so touch/tap opens the menu
          reliably — the custom-button-over-Slot nesting was the leading
          hypothesis for the mobile tap failure. data-testid/data-status/
          aria-label are preserved (tests + screen readers depend on them). */}
      <DropdownMenuTrigger
        data-testid="bot-status-chip"
        data-status={opStatus}
        aria-label={operationalLabel}
        className={cn(
          'inline-flex items-center rounded-full bg-muted hover:bg-muted/60 transition-colors',
          'h-8 w-8 justify-center md:w-auto md:gap-2 md:px-3 md:justify-start',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          // F098: ≥44px tap target without growing the 32px dot
          HIT_AREA_44
        )}
      >
        <span
          className={cn('h-2 w-2 rounded-full', dotClass)}
          aria-hidden="true"
          data-testid="bot-status-chip-dot"
        />
        <span
          data-testid="bot-chip-operational-label"
          className="hidden md:inline text-xs font-medium"
        >
          {operationalLabel}
        </span>
        {/* Usage meter inline when loaded and not offline */}
        {usage && opStatus !== 'offline' && (
          <span className="hidden md:inline ml-1">
            <UsageMeter used={usage.used} cap={usage.cap} mode={usage.mode} variant="chip" />
          </span>
        )}
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-64">
        {/* Status header */}
        <DropdownMenuLabel className="flex flex-col gap-0.5">
          <span className="text-sm font-medium">{operationalLabel}</span>
          {reason && (
            <span className="text-xs font-normal text-muted-foreground">
              {t(`bot.status.reason.${reason}`)}
            </span>
          )}
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        {/* Pause / Resume control */}
        {opStatus === 'active' ? (
          <DropdownMenuItem asChild>
            <button
              data-testid="bot-chip-pause-btn"
              onClick={handlePause}
              disabled={isPausing}
              className="w-full cursor-pointer text-left"
            >
              {t('bot.status.pause')}
            </button>
          </DropdownMenuItem>
        ) : (
          <div className="px-2 py-1 flex flex-col gap-1">
            <button
              data-testid="bot-chip-resume-btn"
              onClick={!blocked ? handleResume : undefined}
              disabled={blocked || isResuming}
              className={cn(
                'w-full text-left text-sm rounded-sm px-2 py-1.5 transition-colors',
                blocked ? 'opacity-50 cursor-not-allowed' : 'hover:bg-muted/60 cursor-pointer'
              )}
            >
              {t('bot.status.resume')}
            </button>
            {blocked && (
              <p
                data-testid="bot-chip-resume-blocked"
                className="text-xs text-muted-foreground px-2"
              >
                {t('bot.status.resume_blocked')}
              </p>
            )}
          </div>
        )}

        {/* CTAs */}
        {(showBillingCta || showReconnectCta) && <DropdownMenuSeparator />}

        {showBillingCta && (
          <DropdownMenuItem asChild>
            <Link to="/settings" data-testid="bot-chip-billing-link" className="cursor-pointer">
              {t('bot.status.view_invoices')}
            </Link>
          </DropdownMenuItem>
        )}

        {showReconnectCta && (
          <DropdownMenuItem asChild>
            <Link
              to="/"
              state={{ expandStep: 'meta_oauth' }}
              data-testid="bot-chip-reconnect-link"
              className="cursor-pointer"
            >
              {t('bot.status.reconnect')}
            </Link>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ── Combined BotChip — pre-live = onboarding, live = operational ─────────────

function BotChip() {
  const { state, loading } = useOnboardingState();

  // F067: while OnboardingContext is loading (or on fetch error), don't
  // render any status — show a neutral placeholder instead.
  if (loading || state === null) {
    return <LoadingChip />;
  }

  const onboardingStatus = deriveBotStatus(state);

  if (onboardingStatus !== 'live') {
    return <OnboardingChip />;
  }

  return <OperationalChip />;
}

export function AppLayout() {
  const { t } = useTranslation();
  // F067 stretch: controlled Sheet so nav clicks close it on mobile.
  const [sheetOpen, setSheetOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="h-14 border-b border-border flex items-center px-4 md:px-6 gap-4">
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden h-11 w-11"
              aria-label={t('nav.open_menu')}
            >
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-64">
            <SheetTitle className="sr-only">{t('nav.menu_title')}</SheetTitle>
            <Sidebar onNavigate={() => setSheetOpen(false)} />
          </SheetContent>
        </Sheet>

        <h1 className="text-sm font-semibold">Babilu</h1>

        <div className="ml-auto flex items-center gap-3">
          {/* F043: notification bell — leftmost trailing item per shared header IA contract */}
          <NotificationBell />
          {/* F045: combined BotChip — pre-live=onboarding, live=operational+usage (F046 meter inline) */}
          <BotChip />
          {/* F049: discreet "Precisa de ajuda?" entry — docs + support email */}
          <SupportMenu />
          <UserMenu />
        </div>
      </header>

      <VerificationBanner />

      <div className="flex-1 flex">
        <aside className="hidden md:flex w-64 border-r border-border flex-col">
          <Sidebar />
        </aside>

        {/* No overflow-auto here: the shell is min-h-screen, so the document is
            the real scroller and this would never clip — but any overflow value
            other than visible still makes <main> the nearest scrollport for
            position:sticky, silently defeating the wizard's sticky nav footer
            (F149). min-w-0 replaces the automatic-minimum-size reset that
            overflow-auto used to provide on this flex item — without it, wide
            content propagates its min-content width to the page. Horizontal-
            overflow safety is owned by the per-card rules (docs/07 gotcha #25)
            + the overflow sweep, not by this container. */}
        <main className="flex-1 min-w-0">
          <div className="mx-auto w-full max-w-7xl p-4 md:p-8 lg:p-12">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
